import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Button, Typography, useTheme, Drawer, useMediaQuery, IconButton, AppBar, Toolbar, InputAdornment, CircularProgress, alpha } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { CheckOutlined } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { useAddUserResponseDialogStyles } from './useAddUserResponseDialogStyles';
import CustomDialog from '@ui/components/common/Dialog';
import HTimeline from '@ui/components/Timeline';
import fieldRenderers from '@ui/pages/EventRegistration/RegistrationForm/fieldRenderers';
import { REGISTRATION_FIELD_FILL_BG } from '@ui/pages/EventRegistration/RegistrationForm/registrationFormFieldStyles';
import AdditionalAttendeesGroup from '@ui/components/AdditionalAttendeesGroup';
import { useAppSelector, useAppDispatch } from '@application/store/useStore';
import { APIStatus } from '@application/enums/api-status';
import { getAllActiveResponsesByEventIdGroupedByRegistrationAsync } from '@application/features/vdkEventResponses/vdkEventResponsesThunk';
import { FieldType } from '@application/enums/field-types';
import { checkEmailExistsAsync, createFormResponseAsync } from '@application/features/formResponse/formResponseThunk';
import { RegistrationType } from '@application/enums/registration-type';
import { getVDKEventByIdForRegistrationAsync } from '@application/features/vdkEvent/VDKEventThunk';
import { checkPickupLocationAvailabilityAsync } from '@application/features/pickupLocation/pickupLocationThunk';
const AddUserResponseDialog = ({ open, onClose, eventId, paginationModel }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const styles = useAddUserResponseDialogStyles();
    const dispatch = useAppDispatch();
    // Get steps, fields, and responses from Redux store
    const { stepsFieldsResponses, status } = useAppSelector((state) => state.vdkEventResponse);
    // Get event data to access isExternalAllowed
    const { VDKEventsForEventDetails, VDKEventForRegistration, VDKEvents } = useAppSelector((state) => state.VDKEvent);
    const currentEvent = VDKEventsForEventDetails || (VDKEvents ?? []).find((e) => e.id === eventId);
    // Store referredBy fields grouped by parent company dropdown ID
    const [referredByFieldsByParent, setReferredByFieldsByParent] = useState({});
    // Add loading state for save operation
    const [isSaving, setIsSaving] = useState(false);
    const [hasBlockingEmail, setHasBlockingEmail] = useState(false);
    const [emailLoading, setEmailLoading] = useState({});
    const [emailServerError, setEmailServerError] = useState({});
    const emailDebounceRef = useRef({});
    const [hasBlockingPickupLocation, setHasBlockingPickupLocation] = useState(false);
    const [pickupLocationLoading, setPickupLocationLoading] = useState({});
    const [pickupLocationError, setPickupLocationError] = useState({});
    useEffect(() => {
        const fetchEventData = async () => {
            if (eventId) {
                try {
                    const request = {
                        id: eventId
                    };
                    const event = await dispatch(getVDKEventByIdForRegistrationAsync(request)).unwrap();
                }
                catch (error) { }
            }
            else {
            }
        };
        fetchEventData();
    }, [eventId, dispatch]);
    // Group referredBy fields by parent company dropdown when VDKEventForRegistration is loaded
    useEffect(() => {
        if (VDKEventForRegistration?.formSteps) {
            const referredByByParent = {};
            VDKEventForRegistration.formSteps.forEach((step) => {
                step.formFields?.forEach((field) => {
                    const parentId = field.relatedFormFieldId || field.RelatedFormFieldId;
                    if (parentId) {
                        if (!referredByByParent[parentId]) {
                            referredByByParent[parentId] = [];
                        }
                        referredByByParent[parentId].push({
                            ...field,
                            fieldType: field.fieldType?.toString() || field.fieldTypeId?.toString() || '1',
                            isRequired: field.isRequired || field.required || false,
                            isEnabled: field.isEnabled === true || field.isEnabled === undefined,
                            fieldOptions: field.fieldOptions || field.options || []
                        });
                    }
                });
            });
            setReferredByFieldsByParent(referredByByParent);
        }
    }, [VDKEventForRegistration]);
    const { control, handleSubmit, watch, setValue, getValues, reset, setError, clearErrors, formState: { errors } } = useForm({
        mode: 'all',
        reValidateMode: 'onChange'
    });
    // Clear form data when dialog opens (for add mode, we want a clean form)
    useEffect(() => {
        if (open) {
            reset();
            setHasBlockingEmail(false);
            setEmailLoading({});
            setEmailServerError({});
            setHasBlockingPickupLocation(false);
            setPickupLocationLoading({});
            setPickupLocationError({});
        }
    }, [open, reset]);
    // Get email field IDs
    const emailFieldIds = useMemo(() => {
        return (VDKEventForRegistration?.formSteps || []).flatMap((formStep) => (formStep.formFields || [])
            .filter((field) => field.fieldType === '2')
            .map((field) => field.id));
    }, [VDKEventForRegistration?.formSteps]);
    // Email validation effect
    useEffect(() => {
        if (!eventId || emailFieldIds.length === 0)
            return;
        const checkEmail = async (name, emailValue) => {
            const stopLoading = () => setEmailLoading((prev) => ({
                ...prev,
                [name]: false
            }));
            if (!emailValue || emailValue.trim() === '') {
                stopLoading();
                setHasBlockingEmail(false);
                setEmailServerError((prev) => ({ ...prev, [name]: null }));
                clearErrors(name);
                return;
            }
            const isValidFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue);
            if (!isValidFormat) {
                stopLoading();
                setHasBlockingEmail(false);
                setEmailServerError((prev) => ({ ...prev, [name]: 'Invalid email' }));
                setError(name, {
                    type: 'pattern',
                    message: 'Invalid email'
                });
                return;
            }
            clearErrors(name);
            setHasBlockingEmail(false);
            setEmailLoading((prev) => ({
                ...prev,
                [name]: true
            }));
            try {
                const response = await dispatch(checkEmailExistsAsync({ eventId: eventId, email: emailValue })).unwrap();
                const emailExists = response?.data?.emailExists;
                setHasBlockingEmail(emailExists || false);
                setEmailServerError((prev) => ({
                    ...prev,
                    [name]: emailExists
                        ? response?.message || 'This email is already registered for this event'
                        : null
                }));
                if (emailExists) {
                    setError(name, {
                        type: 'manual',
                        message: response?.message || 'This email is already registered for this event'
                    });
                }
                else {
                    clearErrors(name);
                }
            }
            catch (error) {
                const apiError = error;
                setEmailServerError((prev) => ({
                    ...prev,
                    [name]: apiError?.validationErrors?.[0] || apiError?.error || 'Unable to verify email right now'
                }));
                setError(name, {
                    type: 'manual',
                    message: apiError?.validationErrors?.[0] || apiError?.error || 'Unable to verify email right now'
                });
            }
            finally {
                stopLoading();
            }
        };
        // --- Watch user typing ---
        const subscription = watch((value, { name }) => {
            if (!name || !emailFieldIds.includes(name))
                return;
            const emailValue = value?.[name];
            if (emailDebounceRef.current[name]) {
                clearTimeout(emailDebounceRef.current[name]);
            }
            emailDebounceRef.current[name] = setTimeout(() => {
                void checkEmail(name, emailValue);
            }, 500);
        });
        // --- Run on mount for existing values (page reload) ---
        emailFieldIds.forEach((name) => {
            const emailValue = getValues(name);
            if (emailValue) {
                void checkEmail(name, emailValue);
            }
        });
        return () => {
            subscription.unsubscribe();
            Object.values(emailDebounceRef.current).forEach((timer) => clearTimeout(timer));
        };
    }, [watch, emailFieldIds, eventId, dispatch, setError, clearErrors, getValues]);
    const isEmailCheckInProgress = useMemo(() => Object.values(emailLoading).some(Boolean), [emailLoading]);
    const pickupLocationFieldIds = useMemo(() => {
        return (VDKEventForRegistration?.formSteps || []).flatMap((formStep) => (formStep.formFields || [])
            .filter((field) => field.fieldType === FieldType.PICKUPLOCATION)
            .map((field) => field.id));
    }, [VDKEventForRegistration?.formSteps]);
    const checkPickupLocationById = async (name, selectedValue) => {
        if (!selectedValue || selectedValue.trim() === '') {
            setHasBlockingPickupLocation(false);
            setPickupLocationError((prev) => ({ ...prev, [name]: null }));
            clearErrors(name);
            return false;
        }
        // Resolve GUID from fieldOptions using sequential value
        const allFields = (VDKEventForRegistration?.formSteps || []).flatMap((s) => s.formFields || []);
        const pickupField = allFields.find((f) => f.id === name);
        // Build options from pickupLocations
        const pickupOptions = (VDKEventForRegistration?.pickupLocations || []).map((loc, index) => ({
            id: loc.id,
            label: loc.name,
            value: String(index + 1)
        }));
        const matchedOption = pickupOptions.find((opt) => String(opt.value) === String(selectedValue));
        if (!matchedOption) {
            setValue(name, undefined, {
                shouldDirty: false,
                shouldTouch: false,
                shouldValidate: false
            });
            setHasBlockingPickupLocation(false);
            setPickupLocationError((prev) => ({ ...prev, [name]: null }));
            clearErrors(name);
            return false;
        }
        const locationId = String(matchedOption.id);
        clearErrors(name);
        setHasBlockingPickupLocation(false);
        setPickupLocationLoading((prev) => ({ ...prev, [name]: true }));
        try {
            const response = await dispatch(checkPickupLocationAvailabilityAsync(locationId)).unwrap();
            const isClosed = response?.data?.isClosed;
            setHasBlockingPickupLocation(isClosed || false);
            setPickupLocationError((prev) => ({
                ...prev,
                [name]: isClosed
                    ? response?.message || t('eventRegistration.form.pickupLocationClosed')
                    : null
            }));
            if (isClosed) {
                setError(name, {
                    type: 'manual',
                    message: response?.message || t('eventRegistration.form.pickupLocationClosed')
                });
            }
            else {
                clearErrors(name);
            }
            return isClosed || false;
        }
        catch (error) {
            const apiError = error;
            const errorMsg = apiError?.validationErrors?.[0] ||
                apiError?.error ||
                t('eventRegistration.form.pickupLocationCheckFailed');
            setPickupLocationError((prev) => ({ ...prev, [name]: errorMsg }));
            setError(name, {
                type: 'manual',
                message: errorMsg || 'Unable to verify pickup location right now'
            });
            return true;
        }
        finally {
            setPickupLocationLoading((prev) => ({ ...prev, [name]: false }));
        }
    };
    useEffect(() => {
        if (!eventId || pickupLocationFieldIds.length === 0)
            return;
        const subscription = watch((value, { name }) => {
            if (!name || !pickupLocationFieldIds.includes(name))
                return;
            void checkPickupLocationById(name, value?.[name]);
        });
        return () => subscription.unsubscribe();
    }, [watch, pickupLocationFieldIds, eventId]);
    const isPickupLocationLoading = useMemo(() => Object.values(pickupLocationLoading).some(Boolean), [pickupLocationLoading]);
    const validatePickupLocations = async () => {
        const allFields = (VDKEventForRegistration?.formSteps || []).flatMap((s) => s.formFields || []);
        const pickupFields = allFields.filter((f) => f.fieldType === FieldType.PICKUPLOCATION);
        if (pickupFields.length === 0)
            return false;
        const results = await Promise.all(pickupFields.map((field) => checkPickupLocationById(field.id, getValues(field.id))));
        return results.some(Boolean);
    };
    const getAllFormFields = () => (VDKEventForRegistration?.formSteps || []).flatMap((step) => step.formFields || []);
    const getOptionToParentFieldMap = () => {
        const map = {};
        getAllFormFields().forEach((field) => {
            (field.fieldOptions || field.options || []).forEach((option) => {
                if (option?.id) {
                    map[String(option.id)] = field.id;
                }
            });
        });
        return map;
    };
    const getSelectedOptionIds = (field) => {
        const currentValue = watch(field.id);
        const toComparableValues = (value) => {
            if (typeof value === 'boolean')
                return value ? ['1', 'true'] : ['0', 'false'];
            return [String(value)];
        };
        const selectedValues = Array.isArray(currentValue)
            ? currentValue.flatMap((v) => toComparableValues(v))
            : currentValue !== undefined && currentValue !== null && currentValue !== ''
                ? toComparableValues(currentValue)
                : [];
        const selectedValuesSet = new Set(selectedValues.map((v) => String(v).toLowerCase()));
        if (selectedValues.length === 0)
            return [];
        const fieldOptions = field.fieldOptions || field.options || [];
        return fieldOptions
            .filter((option) => selectedValuesSet.has(String(option.value).toLowerCase()))
            .map((option) => String(option.id));
    };
    const isBranchingFieldVisible = (field) => {
        const parentOptionIds = Array.isArray(field.parentOptionIds)
            ? field.parentOptionIds
            : (field.parentOptionId ?? field.ParentOptionId)
                ? [String(field.parentOptionId ?? field.ParentOptionId)]
                : [];
        if (parentOptionIds.length === 0)
            return true;
        const optionToParentField = getOptionToParentFieldMap();
        const parentFieldId = optionToParentField[String(parentOptionIds[0])];
        if (!parentFieldId)
            return false;
        const parentField = getAllFormFields().find((f) => f.id === parentFieldId);
        if (!parentField)
            return false;
        const selectedParentOptionIds = getSelectedOptionIds(parentField);
        return parentOptionIds.some((id) => selectedParentOptionIds.includes(String(id)));
    };
    const getFieldProps = (field, isReferredByField = false, isEmailReferredByField = false) => {
        const common = {
            key: field.id,
            name: field.id,
            control,
            fieldType: field.fieldType,
            label: field.label,
            bgColor: theme.palette.common.grey8,
            border: `1px solid ${alpha(theme.palette.grey[500], 0.12)}`,
            selectedTone: 'darker',
            defaultValue: undefined,
            rules: field.fieldType === '19'
                ? {} // no validation for undertaking fields
                : {
                    required: field.isRequired
                        ? field.requiredErrorMsg || 'This field is required'
                        : false,
                    validate: (value) => {
                        if (!isBranchingFieldVisible(field)) {
                            return true;
                        }
                        // Email validation for referredBy email field (runs even if field is optional)
                        if (isEmailReferredByField) {
                            if (value && typeof value === 'string' && value.trim() !== '') {
                                const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                if (!emailPattern.test(value.trim())) {
                                    return 'Invalid email address';
                                }
                            }
                        }
                        // Only validate if the field is required
                        if (!field.isRequired)
                            return emailServerError[field.id] || true;
                        // Check for empty values
                        if (!value || value === '') {
                            return field.requiredErrorMsg || 'This field is required';
                        }
                        // Check for empty arrays (for multi-select fields)
                        if (Array.isArray(value) && value.length === 0) {
                            return field.requiredErrorMsg || 'This field is required';
                        }
                        // Check for whitespace-only strings
                        if (typeof value === 'string' && value.trim() === '') {
                            return field.requiredErrorMsg || 'This field is required';
                        }
                        return emailServerError[field.id] || true;
                    }
                }
        };
        if (field.fieldOptions) {
            common.options = field.fieldOptions.map((opt) => ({
                label: opt.label,
                value: String(opt.value),
                id: opt.id
            }));
        }
        if (field.fieldType === '2') {
            const baseValidate = common.rules?.validate;
            common.rules = {
                ...common.rules,
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email' },
                validate: (value) => {
                    const baseResult = baseValidate ? baseValidate(value) : true;
                    if (baseResult !== true)
                        return baseResult;
                    return emailServerError[field.id] || true;
                }
            };
            common.InputProps = {
                ...(common.InputProps || {}),
                endAdornment: emailLoading[field.id] ? (_jsx(InputAdornment, { position: 'end', children: _jsx(CircularProgress, { size: 18 }) })) : undefined
            };
        }
        if (field.fieldType === '6') {
            common.multiline = true;
            common.rows = 4;
        }
        if (field.fieldType === '8' || String(field.fieldType) === String(FieldType.CHOICE)) {
            common.bgColor = REGISTRATION_FIELD_FILL_BG;
            common.notSelectedBgColor = 'transparent';
        }
        if (field.fieldType === '5') {
            common.selectedTone = 'darker';
        }
        if (field.fieldType === '4') {
            common.selectedTone = 'darker';
        }
        // Undertaking field (field type 19) - NOT readonly in add dialog
        if (field.fieldType === '19') {
            const undertakingFile = VDKEventForRegistration?.undertakingFiles?.[0];
            common.title = field.documentTitle || field.label || 'Document';
            common.fileName = undertakingFile?.name || 'document.pdf';
            common.content = field.documentContent || '';
            common.pdfUrl = undertakingFile?.url || field.pdfUrl || field.documentUrl || '';
            common.defaultAcknowledgeReason =
                field.undertakingToggleLabel ||
                    'Yes, I acknowledge that I understand all the rules and regulations.';
            common.defaultDeclineReason = 'I decline this undertaking due to personal concerns.';
            common.undertakingToggleLabel = field.undertakingToggleLabel;
            common.undertakingAlertNote = field.undertakingAlertNote;
            common.readonly = true;
            // Set the reason field name to match the field ID + "_reason"
            common.reasonName = `${field.id}_reason`;
            // Pre-set value to 'Yes' if not already set and avoid Infinite loop
            const currentVal = control._formValues?.[field.id]; // access form's current value
            if (!currentVal) {
                setTimeout(() => {
                    setValue(field.id, 'Yes', { shouldValidate: false, shouldDirty: false });
                    setValue(`${field.id}_reason`, 'Signature Pending', {
                        shouldValidate: false,
                        shouldDirty: false
                    });
                }, 0);
            }
        }
        // EAutocompleteWithCreate (field type 18) - Company dropdown
        if (field.fieldType === '18') {
            const isExternalAllowed = currentEvent?.isExternalAllowed || false;
            common.allowCreate = isExternalAllowed;
            common.createOptionText = t('common.add');
            common.onCreateOption = (label) => ({
                label: label,
                value: isExternalAllowed
                    ? label.replace(/\s+/g, '-')
                    : label.toLowerCase().replace(/\s+/g, '-'),
                isNew: true
            });
            // For company dropdown, populate options ONLY from event companies
            let freshOptions = [];
            // Only add event companies as options
            if (currentEvent?.eventCompanies && currentEvent.eventCompanies.length > 0) {
                const eventCompanyOptions = currentEvent.eventCompanies.map((company, index) => ({
                    id: company.value,
                    label: company.label,
                    value: String(index + 1),
                    isNew: false
                }));
                freshOptions = [...eventCompanyOptions];
            }
            // Assign the fresh options array
            common.options = freshOptions;
        }
        if (field.fieldType === '20') {
            common.multiple = false;
            common.options = (VDKEventForRegistration?.pickupLocations || []).map((loc, index) => ({
                id: loc.id,
                label: loc.name,
                description: loc.description ?? loc.address ?? '',
                value: String(index + 1),
                isClosed: loc.isClosed
            }));
        }
        return common;
    };
    const renderFieldWithResponse = (field) => {
        if (String(field.fieldType) === String(FieldType.ADDITIONAL_ATTENDEES)) {
            return (_jsx(AdditionalAttendeesGroup, { fieldId: field.id, label: field.label, maxAdditionalAttendees: field.maxAdditionalAttendees ?? field.MaxAdditionalAttendees ?? 2, maxAdditionalAttendeesReachedMessage: field.maxAdditionalAttendeesReachedMessage ?? field.MaxAdditionalAttendeesReachedMessage, subFieldDefinitions: (referredByFieldsByParent[field.id] || [])
                    .slice()
                    .sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0))
                    .map((subField) => ({
                    id: subField.id,
                    label: subField.label,
                    fieldType: subField.fieldType,
                    isRequired: Boolean(subField.isRequired),
                    fieldOptions: subField.fieldOptions || []
                })), control: control }, field.id));
        }
        const FieldComponent = fieldRenderers[field.fieldType];
        if (!FieldComponent) {
            return (_jsxs(Box, { sx: { width: '100%', mb: 2 }, children: [_jsx(Typography, { variant: 'body1', sx: { fontWeight: 600 }, children: field.label }), _jsxs(Typography, { variant: 'body2', color: 'text.secondary', children: ["Field type \"", field.fieldType, "\" not supported"] })] }, field.id));
        }
        const { key, ...restProps } = getFieldProps(field);
        const fieldError = errors[field.id]?.message;
        return _jsx(FieldComponent, { error: fieldError, ...restProps }, `${key}-${field.id}`);
    };
    const onSubmit = async (data) => {
        if (!VDKEventForRegistration?.formSteps || VDKEventForRegistration?.formSteps.length == 0) {
            toast.error(t('events.eventDetails.responsesTab.missingFormData') ||
                'Missing form data. Please try again.');
            return false;
        }
        if (!VDKEventForRegistration?.formSteps[0].formFields ||
            VDKEventForRegistration?.formSteps[0].formFields.length == 0) {
            toast.error(t('events.eventDetails.responsesTab.missingFormData') ||
                'Missing form data. Please try again.');
            return false;
        }
        setIsSaving(true);
        try {
            // Transform form data to the format expected by the API
            const stepWiseData = VDKEventForRegistration?.formSteps.map((step) => {
                const stepFields = [];
                // Process regular fields first (exclude referredBy fields)
                step.formFields
                    ?.filter((f) => !`${f.id}`.endsWith('_reason') &&
                    !f.relatedFormFieldId &&
                    !f.RelatedFormFieldId &&
                    isBranchingFieldVisible(f)) // skip reason-only fields and referredBy fields
                    .forEach((f) => {
                    const fieldId = f.id;
                    const value = data[fieldId];
                    // Find the field definition to check if it has options
                    let fieldDefinition = null;
                    VDKEventForRegistration?.formSteps.forEach((step) => {
                        const field = step.formFields?.find((f) => f.id === fieldId);
                        if (field)
                            fieldDefinition = field;
                    });
                    // Additional Attendees: each person's answer to each real sub-question field is its
                    // own ordinary entry, tagged with which repeated person (instanceIndex) it belongs to -
                    // not a single JSON blob on the parent field.
                    if (fieldDefinition?.fieldType === FieldType.ADDITIONAL_ATTENDEES) {
                        const people = value?.people || [];
                        const subFieldDefs = referredByFieldsByParent[fieldId] || [];
                        people.forEach((person, personIndex) => {
                            if (!person)
                                return;
                            subFieldDefs.forEach((subField) => {
                                const subValue = person[subField.id];
                                if (subValue === undefined || subValue === '')
                                    return;
                                stepFields.push({
                                    fieldId: subField.id,
                                    value: subValue,
                                    instanceIndex: personIndex
                                });
                            });
                        });
                        return;
                    }
                    const fieldData = {
                        fieldId,
                        value: Array.isArray(value)
                            ? JSON.stringify(value)
                            : value === null || value === undefined || value === ''
                                ? null
                                : String(value)
                    };
                    // Handle undertaking fields (fieldType 19) specially
                    if (fieldDefinition?.fieldType === FieldType.UNDERTAKING) {
                        const reasonFieldName = `${fieldId}_reason`;
                        const acknowledgmentReason = data[reasonFieldName] || '';
                        fieldData.acknowledgmentReason = acknowledgmentReason;
                        fieldData.value = value ? 'true' : 'false';
                    }
                    else if (fieldDefinition?.fieldType === FieldType.PICKUPLOCATION) {
                        if (value && value !== '' && value !== null) {
                            const stringValue = String(value);
                            const pickupOptions = (VDKEventForRegistration?.pickupLocations || []).map((loc, index) => ({
                                id: loc.id,
                                label: loc.name,
                                value: String(index + 1)
                            }));
                            const selectedOption = pickupOptions.find((opt) => String(opt.value) === stringValue);
                            if (selectedOption) {
                                fieldData.options = [
                                    {
                                        optionId: selectedOption.id,
                                        optionName: selectedOption.label,
                                        value: selectedOption.value
                                    }
                                ];
                            }
                            else {
                                fieldData.value = null;
                            }
                        }
                        else {
                            fieldData.value = null;
                        }
                    }
                    // Handle company dropdown fields (fieldType 18) specially
                    else if (fieldDefinition?.fieldType === FieldType.COMPANYDROPDOWN) {
                        if (value && value !== '' && value !== null) {
                            const stringValue = String(value);
                            // Find the selected option from the fresh options (event companies)
                            let selectedOption = null;
                            // Get event companies from currentEvent
                            if (currentEvent?.eventCompanies && currentEvent.eventCompanies.length > 0) {
                                const eventCompanyOptions = currentEvent.eventCompanies.map((company, index) => ({
                                    id: company.value,
                                    label: company.label,
                                    value: String(index + 1),
                                    isNew: false
                                }));
                                selectedOption = eventCompanyOptions.find((opt) => String(opt.value) === stringValue);
                            }
                            if (selectedOption) {
                                // This is a predefined event company - send the option data with companyId
                                fieldData.options = [
                                    {
                                        optionId: selectedOption.id,
                                        optionName: selectedOption.label,
                                        value: selectedOption.value,
                                        companyId: selectedOption.id
                                    }
                                ];
                            }
                            else {
                                // This is a newly added company - send as new option
                                // For new companies, we need to generate a sequential value
                                const nextValue = (currentEvent?.eventCompanies?.length || 0) + 1;
                                fieldData.options = [
                                    {
                                        optionId: null,
                                        optionName: stringValue,
                                        value: nextValue,
                                        companyId: null // No companyId for new options
                                    }
                                ];
                            }
                        }
                        else {
                            // No value selected - send null
                            fieldData.value = null;
                        }
                    }
                    // Handle other fields with options (multi-select, etc.)
                    else if (fieldDefinition?.fieldOptions && Array.isArray(value)) {
                        const selectedOptions = fieldDefinition.fieldOptions
                            .filter((opt) => value.includes(String(opt.value)))
                            .map((opt) => ({
                            optionId: opt.id,
                            optionName: opt.label,
                            value: opt.value
                        }));
                        if (selectedOptions.length > 0) {
                            fieldData.options = selectedOptions;
                        }
                    }
                    stepFields.push(fieldData);
                });
                // Collect referredBy fields for company dropdowns with external company selected AND event is external
                step.formFields
                    ?.filter((f) => f.fieldType === FieldType.COMPANYDROPDOWN)
                    .forEach((f) => {
                    const fieldId = f.id;
                    const value = data[fieldId];
                    const isExternalCompany = value && value !== '' && value !== null && isNaN(Number(value));
                    const isExternalEvent = currentEvent?.isExternalAllowed || false;
                    // Only collect referredBy fields when event is external AND external company is selected
                    if (isExternalEvent && isExternalCompany && referredByFieldsByParent[fieldId]) {
                        const visibleReferredByFields = referredByFieldsByParent[fieldId].filter((refField) => refField.isEnabled === true || refField.isEnabled === undefined);
                        visibleReferredByFields.forEach((refField) => {
                            const refFieldValue = data[refField.id];
                            // Include the field if it has a value (matching the logic for regular fields)
                            if (refFieldValue !== undefined && refFieldValue !== '') {
                                const refFieldData = {
                                    fieldId: refField.id,
                                    value: Array.isArray(refFieldValue)
                                        ? JSON.stringify(refFieldValue)
                                        : refFieldValue === null ||
                                            refFieldValue === undefined ||
                                            refFieldValue === ''
                                            ? null
                                            : String(refFieldValue)
                                };
                                stepFields.push(refFieldData);
                            }
                        });
                    }
                });
                return {
                    stepId: step.id,
                    stepName: step.name,
                    fields: stepFields.filter((fd) => fd !== null)
                };
            });
            const submissionPayload = {
                eventId: eventId,
                formResponses: stepWiseData,
                registrationType: RegistrationType.Admin
            };
            try {
                dispatch(createFormResponseAsync(submissionPayload))
                    .unwrap()
                    .then(() => {
                    // Show success toast
                    toast.success(t('events.eventDetails.responsesTab.responseAddedSuccessfully') ||
                        'Response added successfully!');
                    // Refresh grid data to show latest data
                    if (eventId && paginationModel) {
                        const refreshRequest = {
                            eventId,
                            pageNumber: paginationModel.page + 1,
                            pageSize: paginationModel.pageSize
                        };
                        dispatch(getAllActiveResponsesByEventIdGroupedByRegistrationAsync(refreshRequest));
                    }
                    return true;
                })
                    .catch((error) => {
                    const errorMessage = error?.message ||
                        error?.error ||
                        t('events.eventDetails.responsesTab.failedToAddResponse') ||
                        'Failed to add response. Please try again.';
                    toast.error(errorMessage);
                    return false;
                });
                return true;
            }
            catch (error) {
                return false;
            }
        }
        catch (error) {
            // Show error toast
            const errorMessage = error?.message ||
                error?.error ||
                t('events.eventDetails.responsesTab.failedToAddResponse') ||
                'Failed to add response. Please try again.';
            toast.error(errorMessage);
            return false;
        }
        finally {
            setIsSaving(false);
        }
    };
    const handleSave = async () => {
        const isPickupBlocking = await validatePickupLocations();
        if (isPickupBlocking)
            return;
        handleSubmit(async (data) => {
            const success = await onSubmit(data);
            if (success) {
                onClose();
            }
        })();
    };
    const handleAddAnother = async () => {
        const isPickupBlocking = await validatePickupLocations();
        if (isPickupBlocking)
            return;
        handleSubmit(async (data) => {
            const success = await onSubmit(data);
            if (success) {
                reset();
            }
        })();
    };
    // Create timeline options once to reuse in both desktop and mobile views
    // const timelineOptions = useMemo(
    //   () =>
    //     (VDKEventForRegistration?.formSteps || []).map((step) => ({
    //       step: step.name,
    //       content: (
    //         <Box sx={{ mt: 2, mb: 3 }}>
    //           {[...(step.formFields || [])]
    //             .sort((a: any, b: any) => (a.fieldOrder || 0) - (b.fieldOrder || 0))
    //             .map((field, index) => (
    //               <Box key={field.id} sx={{ mb: index < step.formFields.length - 1 ? '12px' : 0 }}>
    //                 {renderFieldWithResponse(field)}
    //               </Box>
    //             ))}
    //         </Box>
    //       )
    //     })),
    //   [errors, VDKEventForRegistration?.formSteps] // Re-compute when errors or data change
    // );
    const timelineOptions = (VDKEventForRegistration?.formSteps || []).map((step) => ({
        step: step.name,
        content: (_jsx(Box, { sx: { mt: 2, mb: 3 }, children: [...(step.formFields || [])]
                .filter((field) => !field.relatedFormFieldId && !field.RelatedFormFieldId) // Hide referredBy fields from main list
                .filter((field) => isBranchingFieldVisible(field))
                .sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0))
                .map((field, index) => {
                const isCompanyDropdown = field.fieldType === '18';
                const companyValue = isCompanyDropdown ? watch(field.id) : null;
                // External company: value is not a number (original companies use 1, 2, 3...)
                const isExternalCompany = isCompanyDropdown && companyValue && isNaN(Number(companyValue));
                const isExternalEvent = currentEvent?.isExternalAllowed || false;
                // Show referredBy fields only when event is external AND external company is selected
                const shouldShowReferredBy = isExternalEvent && isExternalCompany;
                const referredByFields = shouldShowReferredBy && referredByFieldsByParent[field.id]
                    ? referredByFieldsByParent[field.id]
                        .filter((f) => f.isEnabled === true || f.isEnabled === undefined) // Only show enabled fields
                        .sort((a, b) => (a.fieldOrder || 0) - (b.fieldOrder || 0))
                    : [];
                return (_jsxs(Box, { sx: { mb: index < step.formFields.length - 1 ? '12px' : 0 }, children: [renderFieldWithResponse(field), shouldShowReferredBy && referredByFields.length > 0 && (_jsxs(Box, { sx: { width: '100%', mt: 1 }, children: [_jsx(Typography, { variant: 'body2', sx: {
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        mb: 1.5
                                    }, children: t('events.referredBy') }), _jsx(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 0 }, children: referredByFields.map((refField, refIndex) => {
                                        const RefFieldComponent = fieldRenderers[refField.fieldType];
                                        // The email field is always the second field (index 1) in the sorted referredBy fields
                                        const isEmailReferredByField = refIndex === 1;
                                        const refFieldProps = getFieldProps(refField, true, isEmailReferredByField); // Mark as referredBy field and indicate if it's email
                                        const refFieldError = errors[refField.id]?.message;
                                        return RefFieldComponent ? (_jsx(Box, { sx: { mt: 1 }, children: _jsx(RefFieldComponent, { error: refFieldError, ...refFieldProps }) }, refField.id)) : null;
                                    }) })] }))] }, field.id));
            }) }))
    }));
    const dialogContent = (_jsx(Box, { sx: styles.dialogContentStyle, children: _jsx("form", { onSubmit: handleSubmit(onSubmit), children: _jsx(Box, { sx: { mt: 2, px: 2 }, children: status === APIStatus.PENDING ? (_jsx(Box, { sx: {
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 200
                    }, children: _jsx(Typography, { children: "Loading form data..." }) })) : VDKEventForRegistration?.formSteps &&
                    VDKEventForRegistration?.formSteps.length > 0 ? (_jsx(HTimeline, { options: timelineOptions })) : (_jsx(Box, { sx: {
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: 200
                    }, children: _jsx(Typography, { children: "No form data available" }) })) }) }) }));
    const actionButtons = (_jsxs(_Fragment, { children: [_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', onClick: onClose, disabled: isSaving, children: t('actions.cancel') }), _jsx(Button, { size: 'large', variant: 'outlined', color: 'inherit', onClick: handleAddAnother, disabled: hasBlockingEmail ||
                    isEmailCheckInProgress ||
                    hasBlockingPickupLocation ||
                    isPickupLocationLoading, children: t('actions.addAndNew') }), _jsx(Button, { variant: 'contained', color: 'inherit', size: 'large', endIcon: _jsx(CheckOutlined, {}), onClick: handleSave, disabled: isSaving ||
                    hasBlockingEmail ||
                    isEmailCheckInProgress ||
                    hasBlockingPickupLocation ||
                    isPickupLocationLoading, children: isSaving ? t('actions.adding') || 'Adding...' : t('actions.addNow') || 'Add Now' })] }));
    if (isMobile) {
        return (_jsxs(Drawer, { anchor: 'bottom', open: open, onClose: onClose, PaperProps: { sx: styles.mobileDrawerStyle }, children: [_jsx(AppBar, { position: 'sticky', elevation: 0, sx: styles.appBarStyle, children: _jsxs(Toolbar, { sx: styles.mobileToolbarStyle, children: [_jsx(Typography, { variant: 'h6', color: 'text.primary', fontWeight: 600, children: t('events.eventDetails.responsesTab.addNewAttendee') || 'Add New Attendee' }), _jsx(IconButton, { onClick: onClose, size: 'small', sx: { border: 'none' }, children: _jsx(CloseIcon, {}) })] }) }), _jsx(Box, { sx: { flex: 1, overflow: 'auto' }, children: _jsx("form", { onSubmit: handleSubmit(onSubmit), children: _jsx(Box, { sx: { px: 2 }, children: status === APIStatus.PENDING ? (_jsx(Box, { sx: {
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: 200
                                }, children: _jsx(Typography, { children: "Loading form data..." }) })) : VDKEventForRegistration?.formSteps &&
                                VDKEventForRegistration?.formSteps.length > 0 ? (_jsx(HTimeline, { options: timelineOptions })) : (_jsx(Box, { sx: {
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: 200
                                }, children: _jsx(Typography, { children: "No form data available" }) })) }) }) }), _jsxs(Box, { sx: styles.footerBoxStyle, children: [_jsx(Button, { variant: 'outlined', color: 'inherit', size: 'large', fullWidth: true, onClick: onClose, disabled: isSaving, sx: styles.actionButtonStyle, children: t('actions.cancel') }), _jsx(Button, { size: 'large', variant: 'outlined', color: 'inherit', onClick: handleAddAnother, disabled: hasBlockingEmail ||
                                isEmailCheckInProgress ||
                                hasBlockingPickupLocation ||
                                isPickupLocationLoading, children: t('actions.addAndNew') }), _jsx(Button, { variant: 'contained', color: 'inherit', size: 'large', fullWidth: true, endIcon: _jsx(CheckOutlined, {}), onClick: handleSave, disabled: isSaving ||
                                hasBlockingEmail ||
                                isEmailCheckInProgress ||
                                hasBlockingPickupLocation ||
                                isPickupLocationLoading, sx: styles.actionButtonStyle, children: isSaving ? t('actions.adding') || 'Adding...' : t('actions.addNow') || 'Add Now' })] })] }));
    }
    return (_jsx(CustomDialog, { backgroundVariant: 'white', titleWithIcon: t('events.eventDetails.responsesTab.addNewAttendee') || 'Add New Attendee', open: open, onClose: onClose, maxWidth: 'md', titleIcon: '/assets/images/add-person.png', actions: actionButtons, children: dialogContent }));
};
export default AddUserResponseDialog;
